import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  usePlatformSettings,
  useUpdatePlatformSettings,
  type UpdatePlatformSettingsInput,
} from "@/hooks/usePlatformSettings";

const toNumber = (v: string, fallback: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const BookingDefaultsSection = () => {
  const { data, isLoading } = usePlatformSettings();
  const update = useUpdatePlatformSettings();

  const [form, setForm] = useState({
    defaultCancellationWindowHours: "",
    defaultBookingAdvanceWindowDays: "",
    minBookingNoticeHours: "",
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      defaultCancellationWindowHours: String(
        data.defaultCancellationWindowHours ?? 24,
      ),
      defaultBookingAdvanceWindowDays: String(
        data.defaultBookingAdvanceWindowDays ?? 90,
      ),
      minBookingNoticeHours: String(data.minBookingNoticeHours ?? 2),
    });
  }, [data]);

  const onSave = () => {
    const payload: UpdatePlatformSettingsInput = {
      defaultCancellationWindowHours: toNumber(
        form.defaultCancellationWindowHours,
        24,
      ),
      defaultBookingAdvanceWindowDays: toNumber(
        form.defaultBookingAdvanceWindowDays,
        90,
      ),
      minBookingNoticeHours: toNumber(form.minBookingNoticeHours, 2),
    };
    update.mutate(payload, {
      onSuccess: () => toast.success("Booking defaults saved"),
      onError: (e) => toast.error(e.message),
    });
  };

  if (isLoading) return <Skeleton className="h-32 w-full" />;

  return (
    <section className="rounded-card border border-border p-5 bg-card space-y-4">
      <header>
        <h2 className="text-lg font-semibold">Default booking policy</h2>
        <p className="text-sm text-muted-foreground">
          Fallbacks used when a centre has not configured its own values.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Cancellation window (hours)">
          <Input
            type="number"
            min={0}
            max={720}
            value={form.defaultCancellationWindowHours}
            onChange={(e) =>
              setForm({
                ...form,
                defaultCancellationWindowHours: e.target.value,
              })
            }
          />
        </Field>
        <Field label="Advance booking window (days)">
          <Input
            type="number"
            min={1}
            max={365}
            value={form.defaultBookingAdvanceWindowDays}
            onChange={(e) =>
              setForm({
                ...form,
                defaultBookingAdvanceWindowDays: e.target.value,
              })
            }
          />
        </Field>
        <Field label="Minimum booking notice (hours)">
          <Input
            type="number"
            min={0}
            max={168}
            value={form.minBookingNoticeHours}
            onChange={(e) =>
              setForm({ ...form, minBookingNoticeHours: e.target.value })
            }
          />
        </Field>
      </div>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={update.isPending}>
          {update.isPending ? "Saving…" : "Save defaults"}
        </Button>
      </div>
    </section>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    {children}
  </div>
);

export default BookingDefaultsSection;
